import {
	Box,
	Callout,
	Heading,
	IconSolidChartBar,
	Stack,
	Text,
} from '@highlight-run/ui/components'
import { useParams } from '@util/react-router/useParams'
import moment from 'moment'
import React, { useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
	Area,
	AreaChart,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from 'recharts'

import {
	useEnsureProjectAnalyticsMutation,
	useProjectAnalyticsQuery,
} from '@/graph/generated/hooks'

const numberFormatter = new Intl.NumberFormat()

const AnalyticsPage = () => {
	const { project_id } = useParams<{ project_id: string }>()
	const navigate = useNavigate()
	const [ensureAnalytics] = useEnsureProjectAnalyticsMutation()
	const range = useMemo(
		() => ({
			startAt: moment().subtract(30, 'days').startOf('day').toDate(),
			endAt: moment().endOf('day').toDate(),
		}),
		[],
	)

	useEffect(() => {
		if (project_id) {
			ensureAnalytics({ variables: { project_id } }).catch(() => {})
		}
	}, [ensureAnalytics, project_id])

	const { data, loading } = useProjectAnalyticsQuery({
		variables: {
			project_id: project_id ?? '',
			start_at: range.startAt.toISOString(),
			end_at: range.endAt.toISOString(),
		},
		skip: !project_id,
		fetchPolicy: 'cache-and-network',
	})

	const analytics = data?.project_analytics
	const summary = analytics?.summary
	const chartData =
		analytics?.pageviews.map((point) => ({
			date: moment(point.x).format('MMM D'),
			pageviews: point.pageviews,
			visitors: point.visitors,
		})) ?? []

	return (
		<Box
			display="flex"
			flexDirection="column"
			height="full"
			overflowY="auto"
			background="default"
			p="32"
			gap="24"
		>
			<Stack direction="column" gap="6">
				<Heading level="h3">Analytics</Heading>
				<Text color="moderate">
					Traffic, sources, pages, and events from the same install
					snippet that powers session replay.
				</Text>
			</Stack>

			{!loading && analytics?.error ? (
				<Callout icon={IconSolidChartBar} title="Analytics unavailable">
					<Text color="moderate">{analytics.error}</Text>
				</Callout>
			) : null}

			<Box
				gap="12"
				style={{
					display: 'grid',
					gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
				}}
			>
				<Stat label="Visitors" value={summary?.visitors} />
				<Stat label="Pageviews" value={summary?.pageviews} />
				<Stat label="Visits" value={summary?.visits} />
				<Stat
					label="Bounce rate"
					value={
						summary && summary.visits > 0
							? `${Math.round((summary.bounces / summary.visits) * 100)}%`
							: undefined
					}
				/>
				<Stat label="Live visitors" value={summary?.active_visitors} />
			</Box>

			<Box border="secondary" borderRadius="8" background="raised" p="16">
				<Stack direction="column" gap="12">
					<Text weight="bold">Pageviews and visitors</Text>
					<Box style={{ height: 280 }}>
						<ResponsiveContainer width="100%" height="100%">
							<AreaChart data={chartData}>
								<XAxis dataKey="date" tickLine={false} />
								<YAxis tickLine={false} width={48} />
								<Tooltip />
								<Area
									type="monotone"
									dataKey="pageviews"
									stroke="#6C5DD3"
									fill="#6C5DD3"
									fillOpacity={0.16}
								/>
								<Area
									type="monotone"
									dataKey="visitors"
									stroke="#0E9F6E"
									fill="#0E9F6E"
									fillOpacity={0.12}
								/>
							</AreaChart>
						</ResponsiveContainer>
					</Box>
				</Stack>
			</Box>

			<Box
				gap="16"
				style={{
					display: 'grid',
					gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
				}}
			>
				<MetricList title="Top pages" items={analytics?.top_pages} />
				<MetricList
					title="Referrers"
					items={analytics?.referrers}
					onItemClick={(name) =>
						navigate(
							`referrer?source=${encodeURIComponent(name)}`,
						)
					}
				/>
				<MetricList title="Browsers" items={analytics?.browsers} />
				<MetricList title="Devices" items={analytics?.devices} />
				<MetricList title="Events" items={analytics?.events} />
			</Box>
		</Box>
	)
}

const Stat = ({
	label,
	value,
}: {
	label: string
	value: number | string | undefined
}) => (
	<Box border="secondary" borderRadius="8" background="raised" p="16">
		<Stack direction="column" gap="4">
			<Text color="moderate">{label}</Text>
			<Text size="large" weight="bold">
				{typeof value === 'number'
					? numberFormatter.format(value)
					: value ?? '-'}
			</Text>
		</Stack>
	</Box>
)

const MetricList = ({
	title,
	items,
	onItemClick,
}: {
	title: string
	items: { name: string; value: number }[] | undefined
	onItemClick?: (name: string) => void
}) => (
	<Box border="secondary" borderRadius="8" background="raised" p="16">
		<Stack direction="column" gap="12">
			<Text weight="bold">{title}</Text>
			<Stack direction="column" gap="8">
				{items?.length ? (
					items.map((item) => (
						<Box
							key={`${title}-${item.name}`}
							display="flex"
							alignItems="center"
							gap="12"
							onClick={
								onItemClick
									? () => onItemClick(item.name)
									: undefined
							}
							style={
								onItemClick
									? { cursor: 'pointer' }
									: undefined
							}
						>
							<Text lines="1" color="informative">
								{item.name}
							</Text>
							<Box ml="auto">
								<Text color="moderate">
									{numberFormatter.format(item.value)}
								</Text>
							</Box>
						</Box>
					))
				) : (
					<Text color="moderate">No data yet</Text>
				)}
			</Stack>
		</Stack>
	</Box>
)

export default AnalyticsPage
